import { useT } from "@transifex/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import phase6Logo from "../../assets/img/p6-logo.svg";
import LoadingSpinner from "../../basic/loadingSpinner/LoadingSpinner";
import ClassLoginForm from "../../complex/classroomLogin/ClassLoginForm";
import { LOGIN_COLUMN_CLASS } from "../../complex/classroomLogin/classMemberFormStyles";
import ClassMemberEntry from "../../complex/classroomLogin/ClassMemberEntry";
import ClassMemberJoinScreens from "../../complex/classroomLogin/ClassMemberJoinScreens";
import LoginHelp from "../../complex/classroomLogin/LoginHelp";
import { ClassMemberJoinSteps, useClassMemberJoin } from "../../complex/classroomLogin/useClassMemberJoin";
import LegalFooter from "../../complex/legalFooter/LegalFooter";
import { useClassMember } from "../../context/ClassMemberContext";
import { useClassDisplayInfo } from "../../hooks/useClassDisplayInfo";
import { LoginErrorVariants, loginErrorVariantFromError } from "../../context/classMemberLoginErrors";
import { getClassroomRoute, getSafeReturnToPath } from "../../helpers/schoolClassHelpers";
import ContentWithHeader, { LogoVariants } from "../../wrapper/ContentWithHeader/ContentWithHeader";

interface RouteParams {
    schoolClassId?: string;
    classMemberCode?: string;
}

const ClassroomLogin = () => {
    const t = useT();
    const history = useHistory();
    const params = useParams<RouteParams>();
    const location = useLocation();
    const { member, isRestoringSession, revalidatedClassCode, login, discardStaleSession } = useClassMember();

    const urlSchoolClassId = (params.schoolClassId ?? "").toUpperCase();
    const urlClassMemberCode = (params.classMemberCode ?? "").toUpperCase();
    const hasBothCodesInUrl = Boolean(urlSchoolClassId && urlClassMemberCode);
    const returnTo = getSafeReturnToPath(new URLSearchParams(location.search).get("returnTo"));
    // ClassroomShell bounces every logged-out visitor of a private class here with its own route as
    // returnTo. That is the normal entry via class link, not a page that demanded a login.
    const isSentFromOtherPage = !!returnTo && returnTo !== getClassroomRoute(urlSchoolClassId);
    // Logged-in class member lands on a URL with a different schoolClassId and no personalCode → log them out
    // so they can enter a new personalCode for the new class. The QR-flow case (both codes in URL)
    // is handled separately by the silent re-login effect below.
    const urlSchoolClassIdMismatch = Boolean(
        member && urlSchoolClassId && !urlClassMemberCode && urlSchoolClassId !== member.schoolClassId.toUpperCase()
    );

    const initialClassCode = urlSchoolClassId || revalidatedClassCode;

    const join = useClassMemberJoin({ schoolClassId: initialClassCode, login });
    const joinInfo = useClassDisplayInfo(initialClassCode);
    const isOnWelcome = join.step === ClassMemberJoinSteps.WELCOME;

    // A failed session revalidation shows no error — the member typed nothing to correct. They only
    // see one once they actually attempt a login.
    const [error, setError] = useState<LoginErrorVariants>(LoginErrorVariants.NONE);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isInstantLoggingIn, setIsInstantLoggingIn] = useState(hasBothCodesInUrl);

    const showClassCodeInput = !initialClassCode;

    const instantLoginAttempted = useRef(false);

    const t_classroom = t("Classroom", { _tags: "ClassMemberLogin, ClassroomLogin" });
    const t_alt_phase6Logo = t("phase6 logo", {
        _tags: "ClassMemberLogin, ClassroomLogin, ScreenReaderOnly",
    });
    const t_sr_loading = t("Logging in", {
        _tags: "ClassMemberLogin, ClassroomLogin, ScreenReaderOnly",
    });
    const t_loginToClassroomFirst = t("Please log in to your classroom first.", {
        _tags: "ClassMemberLogin, ClassroomLogin",
    });

    useEffect(() => {
        if (urlSchoolClassIdMismatch) discardStaleSession();
    }, [urlSchoolClassIdMismatch, discardStaleSession]);

    const handleLogin = useCallback(
        async (classCode: string, personalCode: string) => {
            setIsSubmitting(true);
            try {
                const refreshed = await login(classCode, personalCode);
                history.replace(returnTo ?? getClassroomRoute(refreshed.schoolClassId));
            } catch (e) {
                setError(loginErrorVariantFromError(e));
                setIsInstantLoggingIn(false);
                setIsSubmitting(false);
            }
        },
        [history, login, returnTo]
    );

    const handleStartJoin = () => {
        setError(LoginErrorVariants.NONE);
        join.startJoin();
    };

    useEffect(() => {
        if (isRestoringSession) return;
        if (!hasBothCodesInUrl || instantLoginAttempted.current) return;
        instantLoginAttempted.current = true;
        handleLogin(urlSchoolClassId, urlClassMemberCode);
    }, [isRestoringSession, hasBothCodesInUrl, urlSchoolClassId, urlClassMemberCode, handleLogin]);

    const renderSpinner = (extraClassName: string) => (
        <div
            role="status"
            aria-live="polite"
            className={`flex w-full items-center justify-center ${extraClassName}`}
        >
            <LoadingSpinner />
            <span className="sr-only">{t_sr_loading}</span>
        </div>
    );

    if (isRestoringSession) {
        return (
            <ContentWithHeader logoVariant={LogoVariants.PHASE6}>
                {renderSpinner("min-h-[calc(100vh-60px)]")}
            </ContentWithHeader>
        );
    }

    // Registering already logged the pupil in; the welcome screen holds this redirect until `finish`.
    if (member && !isSubmitting && !isInstantLoggingIn && !urlSchoolClassIdMismatch && !isOnWelcome) {
        return <Redirect to={returnTo ?? getClassroomRoute(member.schoolClassId)} />;
    }

    const loginFormKey = `${initialClassCode}-${urlClassMemberCode}`;

    const renderClassroomHeader = () => (
        <div className="flex flex-wrap items-center justify-center gap-x-s gap-y-xs">
            <img
                src={phase6Logo}
                alt={t_alt_phase6Logo}
                className="h-[34px]"
            />
            <span className="text-xxl text-p6-grey-dark font-semibold">{t_classroom}</span>
        </div>
    );

    const renderClassCodeLogin = () => (
        <div className={`${LOGIN_COLUMN_CLASS} gap-xxl`}>
            {renderClassroomHeader()}
            <div className="flex w-full flex-col items-center gap-s">
                {isSentFromOtherPage && (
                    <p className="self-start text-m text-p6-grey-dark">{t_loginToClassroomFirst}</p>
                )}
                <ClassLoginForm
                    key={loginFormKey}
                    showClassCode
                    initialClassCode={initialClassCode}
                    initialPersonalCode={urlClassMemberCode}
                    error={error}
                    isSubmitting={isSubmitting}
                    onSubmit={handleLogin}
                />
            </div>
            <LoginHelp showClassCode />
        </div>
    );

    const renderEntry = () => {
        if (showClassCodeInput) return renderClassCodeLogin();
        return (
            <ClassMemberEntry
                key={loginFormKey}
                header={renderClassroomHeader()}
                notice={isSentFromOtherPage ? t_loginToClassroomFirst : undefined}
                classCode={initialClassCode}
                initialPersonalCode={urlClassMemberCode}
                loginError={error}
                isLoggingIn={isSubmitting}
                onLogin={handleLogin}
                onJoin={handleStartJoin}
            />
        );
    };

    return (
        <ContentWithHeader
            logoVariant={LogoVariants.PHASE6}
            footer={<LegalFooter />}
        >
            <div className="flex flex-1 flex-col w-full">
                {isInstantLoggingIn ? (
                    renderSpinner("flex-1")
                ) : (
                    <div className="flex flex-1 flex-col items-center pt-xxxxl pb-xxl w-full">
                        <ClassMemberJoinScreens
                            join={join}
                            joinInfo={joinInfo}
                            entry={renderEntry()}
                        />
                    </div>
                )}
            </div>
        </ContentWithHeader>
    );
};

export default ClassroomLogin;
